import React, { useCallback, useEffect, useRef, useState } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import '../styles.css';
import './WysiwygEditor.css';

function markdownToHtml(markdown) {
  try {
    return renderToStaticMarkup(
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{markdown || ''}</ReactMarkdown>,
    );
  } catch (error) {
    console.error('Failed to convert markdown to HTML:', error);
    return markdown || '';
  }
}

function htmlNodeToMarkdown(node, parentNode = null) {
  if (!node) {
    return '';
  }
  if (node.nodeType === Node.TEXT_NODE) {
    return node.textContent || '';
  }
  const children = Array.from(node.childNodes)
    .map((child) => htmlNodeToMarkdown(child, node))
    .join('');

  switch (node.nodeName) {
    case 'H1':
      return `# ${children.trim()}\n\n`;
    case 'H2':
      return `## ${children.trim()}\n\n`;
    case 'H3':
      return `### ${children.trim()}\n\n`;
    case 'H4':
      return `#### ${children.trim()}\n\n`;
    case 'STRONG':
    case 'B':
      return `**${children}**`;
    case 'EM':
    case 'I':
      return `*${children}*`;
    case 'U':
      return `<u>${children}</u>`;
    case 'A': {
      // Check if it's a link card
      if (node.classList.contains('link-card')) {
        // Preserve link card as HTML
        return `\n${node.outerHTML}\n\n`;
      }
      // Regular link
      return `[${children}](${node.getAttribute('href') || ''})`;
    }
    case 'BR':
      return '  \n';
    case 'UL':
      return `${Array.from(node.childNodes)
        .filter(child => child.nodeName === 'LI')
        .map((li) => `- ${htmlNodeToMarkdown(li, node).trim()}\n`)
        .join('')}\n`;
    case 'OL': {
      let counter = 1;
      return `${Array.from(node.childNodes)
        .filter(child => child.nodeName === 'LI')
        .map((li) => `${counter++}. ${htmlNodeToMarkdown(li, node).trim()}\n`)
        .join('')}\n`;
    }
    case 'LI':
      return children;
    case 'IMG': {
      const alt = node.getAttribute('alt') || '';
      const src = node.getAttribute('src') || '';
      return src ? `![${alt}](${src})` : '';
    }
    case 'IFRAME': {
      // Preserve iframe HTML for embeds (YouTube, etc.)
      const outerHTML = node.outerHTML;
      return `\n${outerHTML}\n\n`;
    }
    case 'BLOCKQUOTE': {
      // Check if it's a Twitter embed
      if (node.classList.contains('twitter-tweet')) {
        // Find the next script tag if it exists
        let scriptTag = '';
        if (node.nextSibling && node.nextSibling.nodeName === 'SCRIPT') {
          scriptTag = node.nextSibling.outerHTML;
        }
        return `\n${node.outerHTML}${scriptTag}\n\n`;
      }
      // Regular blockquote - convert to markdown quote
      return `> ${children.trim()}\n\n`;
    }
    case 'SCRIPT': {
      // Skip standalone script tags (they're handled with blockquote)
      const prevSibling = node.previousSibling;
      if (prevSibling && prevSibling.nodeName === 'BLOCKQUOTE' && prevSibling.classList.contains('twitter-tweet')) {
        return '';
      }
      return node.outerHTML;
    }
    case 'DIV':
      // Treat DIVs as paragraphs if they're direct children of BODY
      // This handles cases where contentEditable creates DIVs instead of Ps
      if (parentNode && parentNode.nodeName === 'BODY' && children.trim()) {
        const blocks = Array.from(parentNode.childNodes).filter(
          n => (n.nodeName === 'DIV' || n.nodeName === 'P') && n.textContent?.trim()
        );
        const isLast = blocks[blocks.length - 1] === node;
        return children.trim() + (isLast ? '\n' : '\n\n');
      }
      return children;
    case 'SPAN':
      return children;
    case 'P':
      // Only add double newline if this is not the last paragraph
      if (parentNode && parentNode.nodeName === 'BODY') {
        const blocks = Array.from(parentNode.childNodes).filter(
          n => (n.nodeName === 'P' || n.nodeName === 'DIV') && n.textContent?.trim()
        );
        const isLast = blocks[blocks.length - 1] === node;
        return children.trim() + (isLast ? '\n' : '\n\n');
      }
      return children.trim() + '\n\n';
    default:
      // For other block elements, preserve content without adding extra newlines
      if (children) {
        return children;
      }
      return '';
  }
}

function htmlToMarkdown(html) {
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html || '', 'text/html');
    let markdown = htmlNodeToMarkdown(doc.body);

    // Normalize whitespace:
    // 1. Replace 3+ newlines with exactly 2
    markdown = markdown.replace(/\n{3,}/g, '\n\n');

    // 2. Remove trailing whitespace from each line
    markdown = markdown.split('\n').map(line => line.trimEnd()).join('\n');

    // 3. Ensure no leading/trailing whitespace in the whole content
    markdown = markdown.trim();

    return markdown;
  } catch (error) {
    console.error('Failed to convert HTML to markdown:', error);
    return html || '';
  }
}

function WysiwygEditor({ value, onChange, onImageUpload }) {
  const wrapperRef = useRef(null);
  const editorRef = useRef(null);
  const selectedImageRef = useRef(null);
  const replaceInputRef = useRef(null);
  const [html, setHtml] = useState(() => markdownToHtml(value || ''));
  const [imageMenu, setImageMenu] = useState({ visible: false, x: 0, y: 0 });
  const internalUpdateRef = useRef(false);

  const highlightImage = useCallback((node) => {
    if (selectedImageRef.current && selectedImageRef.current !== node) {
      selectedImageRef.current.classList.remove('wysiwyg-selected');
    }
    if (node) {
      node.classList.add('wysiwyg-selected');
      if (wrapperRef.current) {
        const wrapperRect = wrapperRef.current.getBoundingClientRect();
        const nodeRect = node.getBoundingClientRect();
        setImageMenu({
          visible: true,
          x: nodeRect.left - wrapperRect.left + 8,
          y: nodeRect.top - wrapperRect.top + 8,
        });
      }
    }
    selectedImageRef.current = node || null;
    if (!node) {
      setImageMenu({ visible: false, x: 0, y: 0 });
    }
  }, []);

  useEffect(() => {
    if (internalUpdateRef.current) {
      internalUpdateRef.current = false;
      return;
    }
    const nextHtml = markdownToHtml(value || '');
    setHtml(nextHtml);
    if (editorRef.current && editorRef.current.innerHTML !== nextHtml) {
      editorRef.current.innerHTML = nextHtml;
    }
    highlightImage(null);
  }, [value, highlightImage]);

  const emitChange = useCallback((nextHtml) => {
    internalUpdateRef.current = true;
    const markdown = htmlToMarkdown(nextHtml);

    // Only emit change if markdown actually changed
    // This prevents unnecessary re-renders and stops the degradation cycle
    const currentMarkdown = value || '';
    if (markdown !== currentMarkdown) {
      onChange(markdown);
    } else {
      // Reset the flag since we're not actually changing anything
      internalUpdateRef.current = false;
    }
  }, [onChange, value]);

  const handleInput = (event) => {
    emitChange(event.currentTarget.innerHTML);
  };

  const exec = (command, value = null) => {
    if (!document) {
      return;
    }
    document.execCommand(command, false, value);
    if (editorRef.current) {
      emitChange(editorRef.current.innerHTML);
    }
  };

  const handleCreateLink = () => {
    const url = prompt('リンク先のURLを入力してください:');
    if (url) {
      exec('createLink', url);
    }
  };

  const handleEmbed = () => {
    const url = prompt('埋め込むURLを入力してください（Twitter、YouTube等）:');
    if (!url || !editorRef.current) {
      return;
    }

    let embedHtml = '';

    // YouTube URL detection
    const youtubeMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]+)/);
    if (youtubeMatch) {
      const videoId = youtubeMatch[1];
      embedHtml = `<iframe width="560" height="315" src="https://www.youtube.com/embed/${videoId}" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>`;
    }

    // Twitter/X URL detection
    const twitterMatch = url.match(/(?:twitter\.com|x\.com)\/[\w]+\/status\/(\d+)/);
    if (twitterMatch) {
      embedHtml = `<blockquote class="twitter-tweet"><a href="${url}"></a></blockquote><script async src="https://platform.twitter.com/widgets.js" charset="utf-8"></script>`;
    }

    if (embedHtml) {
      document.execCommand('insertHTML', false, embedHtml);
      emitChange(editorRef.current.innerHTML);
    } else {
      alert('対応していないURLです。Twitter/X または YouTube のURLを入力してください。');
    }
  };

  const fetchMetadata = async (url) => {
    try {
      const response = await fetch('/api/admin/fetch-metadata', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ url }),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch metadata');
      }

      const metadata = await response.json();
      return metadata;
    } catch (error) {
      console.error('Failed to fetch metadata:', error);
      return null;
    }
  };

  const createLinkCard = (metadata) => {
    const { url, title, description, image, siteName } = metadata;
    const hostname = new URL(url).hostname;

    return `<a href="${url}" class="link-card" target="_blank" rel="noopener noreferrer" data-url="${url}" data-title="${title || ''}" data-description="${description || ''}" data-image="${image || ''}" data-sitename="${siteName || hostname}">
      ${image ? `<div class="link-card-image" style="background-image: url('${image}')"></div>` : ''}
      <div class="link-card-content">
        <div class="link-card-title">${title || url}</div>
        ${description ? `<div class="link-card-description">${description}</div>` : ''}
        <div class="link-card-url">${hostname}</div>
      </div>
    </a>`;
  };

  const handlePaste = useCallback(async (event) => {
    const clipboardData = event.clipboardData || window.clipboardData;
    const pastedText = clipboardData.getData('text/plain');

    // Check if pasted text is a URL
    const urlRegex = /^https?:\/\/[^\s]+$/;
    if (!urlRegex.test(pastedText.trim())) {
      return; // Not a URL, let default paste behavior happen
    }

    // Check if it's YouTube or Twitter - let handleEmbed deal with those
    const youtubeMatch = pastedText.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]+)/);
    const twitterMatch = pastedText.match(/(?:twitter\.com|x\.com)\/[\w]+\/status\/(\d+)/);

    if (youtubeMatch || twitterMatch) {
      return; // Let default paste, or user can use embed button
    }

    // Prevent default paste
    event.preventDefault();

    // Fetch metadata and create link card
    const metadata = await fetchMetadata(pastedText.trim());
    if (metadata && metadata.image) {
      const linkCardHtml = createLinkCard(metadata);
      document.execCommand('insertHTML', false, linkCardHtml);
      if (editorRef.current) {
        emitChange(editorRef.current.innerHTML);
      }
    } else {
      // Fallback: just insert as regular link
      document.execCommand('insertHTML', false, `<a href="${pastedText}" target="_blank" rel="noopener noreferrer">${pastedText}</a>`);
      if (editorRef.current) {
        emitChange(editorRef.current.innerHTML);
      }
    }
  }, [emitChange]);
  const handleSurfacePointer = (event) => {
    const target = event.target;
    if (target && target.tagName === 'IMG') {
      event.preventDefault();
      highlightImage(target);
      const range = document.createRange();
      range.selectNode(target);
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);
    } else {
      highlightImage(null);
    }
  };

  const handleImageSelect = async (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = '';
    if (!file || !onImageUpload) {
      return;
    }
    try {
      const url = await onImageUpload(file);
      if (url) {
        const selectedImage = selectedImageRef.current;
        if (selectedImage && editorRef.current && editorRef.current.contains(selectedImage)) {
          selectedImage.setAttribute('src', url);
          highlightImage(selectedImage);
          emitChange(editorRef.current.innerHTML);
        } else {
          document.execCommand('insertImage', false, url);
          if (editorRef.current) {
            emitChange(editorRef.current.innerHTML);
          }
        }
      }
    } catch (error) {
      console.error('Failed to insert image:', error);
    }
  };

  const handleReplaceFile = async (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = '';
    if (!file || !onImageUpload || !selectedImageRef.current) {
      return;
    }
    try {
      const url = await onImageUpload(file);
      if (url) {
        selectedImageRef.current.setAttribute('src', url);
        emitChange(editorRef.current?.innerHTML || '');
        highlightImage(selectedImageRef.current);
      }
    } catch (error) {
      console.error('Failed to replace image:', error);
    }
  };

  const removeSelectedImage = () => {
    if (selectedImageRef.current && editorRef.current?.contains(selectedImageRef.current)) {
      selectedImageRef.current.remove();
      highlightImage(null);
      emitChange(editorRef.current.innerHTML);
    }
  };

  return (
    <div className="wysiwyg-wrapper" ref={wrapperRef}>
      <div className="wysiwyg-toolbar">
        <button type="button" onClick={() => exec('bold')}><strong>B</strong></button>
        <button type="button" onClick={() => exec('italic')}><em>I</em></button>
        <button type="button" onClick={() => exec('formatBlock', 'H1')}>H1</button>
        <button type="button" onClick={() => exec('formatBlock', 'H2')}>H2</button>
        <button type="button" onClick={() => exec('insertUnorderedList')}>• List</button>
        <button type="button" onClick={() => exec('insertOrderedList')}>1. List</button>
        <button type="button" onClick={handleCreateLink} title="選択したテキストをリンクにする">🔗 リンク</button>
        <button type="button" onClick={handleEmbed} title="YouTube、Twitter等を埋め込む">📺 埋め込み</button>
        <label className="wysiwyg-upload">
          画像
          <input type="file" accept="image/*" onChange={handleImageSelect} />
        </label>
      </div>
      <div
        className="wysiwyg-surface"
        contentEditable
        suppressContentEditableWarning
        tabIndex={0}
        ref={editorRef}
        onInput={handleInput}
        onClick={handleSurfacePointer}
        onMouseDown={handleSurfacePointer}
        onPaste={handlePaste}
        dangerouslySetInnerHTML={{ __html: html }}
      />
      {imageMenu.visible && (
        <div
          className="wysiwyg-image-menu"
          style={{ left: imageMenu.x, top: imageMenu.y }}
        >
          <button type="button" onClick={() => replaceInputRef.current?.click()}>差し替え</button>
          <button type="button" onClick={removeSelectedImage}>削除</button>
          <input
            ref={replaceInputRef}
            type="file"
            accept="image/*"
            onChange={handleReplaceFile}
            style={{ display: 'none' }}
          />
        </div>
      )}
    </div>
  );
}

export default WysiwygEditor;
